import moment from 'moment/moment'
import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Booking } from '../../models/Booking'
import { Item } from '../../models/Item'
import { Package } from '../../models/Package'
import { Payment } from '../../models/Payment'
import { User } from '../../models/User'
import { API_BASE } from '../../utils/constants'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import Tag from '../app/design/Tag'
import { RecordStatusChecklist } from '../common/RecordStatusChecklist'
import { BaseRecordView } from '../common/sections'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { Receipt, WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    packageObj: Package
    isWorkflowReviewStep: boolean
    client: User
    item: Item
    bookings?: Booking[]
    payments?: Payment[]
}

export const PackageView = ({
    packageObj,
    isWorkflowReviewStep,
    client,
    item,
    bookings,
    payments,
}: Props) => {
    const navigate = useNavigate()

    const numRemainingSessions = useMemo(() => {
        if (bookings === undefined || packageObj.capacity === undefined) {
            return 0
        }

        return Math.max(packageObj.capacity - bookings.length, 0)
    }, [packageObj, bookings])

    const receipt = useMemo((): Receipt => {
        return {
            items: isWorkflowReviewStep
                ? undefined
                : [
                      {
                          id: '__item__',
                          title: item.name ?? '',
                          subtitle: item.rate === undefined ? 'TBD' : currencyFormat(item.rate),
                          quantity: 1,
                          price: item.rate ?? 0,
                          tertiaryView:
                              item.image?.uri !== undefined ? (
                                  <img src={item.image.uri} className="size-12 rounded-full" />
                              ) : (
                                  <Icon icon="tag.large" className="size-12" background="round" />
                              ),
                      },
                  ],
            modifiers: {
                subtotal: {
                    amount: packageObj.total ?? 0,
                },
                giftCertificate:
                    packageObj.gift !== undefined
                        ? {
                              selectedGift: packageObj.gift,
                          }
                        : undefined,
                discount: packageObj.discount !== undefined ? {} : undefined,
                salesTax:
                    packageObj.taxcode !== undefined
                        ? {
                              amount: packageObj.taxAmount ?? null,
                              parenthetical: `${packageObj.taxcode.rate ?? 0}%`,
                          }
                        : undefined,
                gratuity:
                    ((packageObj.allowAnyPayment ?? '') === '' || !!packageObj.markPaidDate) &&
                    packageObj.gratuityAmount > 0
                        ? {
                              amount: packageObj.gratuityAmount,
                              percent:
                                  packageObj.gratuityType === 'percent'
                                      ? packageObj.gratuity
                                      : undefined,
                          }
                        : undefined,
                surcharge:
                    packageObj.allowSurcharge && packageObj.surchargeAmount > 0
                        ? {
                              amount: packageObj.surchargeAmount,
                          }
                        : undefined,
            },
            total: packageObj.chargeAmount,
        }
    }, [packageObj])

    return (
        <BaseRecordView.Container>
            <BaseRecordView.LeftColumn>
                <Card size="sm">
                    <div className="flex flex-col items-center justify-center gap-y-1">
                        {item.iconConfig === undefined ? undefined : item.iconConfig.type ===
                          'image' ? (
                            <img src={item.iconConfig.uri} className="size-[116px] rounded-full" />
                        ) : (
                            <Icon
                                icon={item.iconConfig.icon}
                                background="round"
                                className="size-[116px]"
                                svgClassName="size-[56px]"
                            />
                        )}

                        <Heading size="sm" className="mt-6 text-center">
                            {packageObj.name}
                        </Heading>

                        {packageObj.expirationString !== undefined && (
                            <Paragraph size="lg" className="font-normal text-gray-600">
                                {packageObj.expirationString}
                            </Paragraph>
                        )}

                        <Paragraph size="lg" className="font-normal text-gray-600">
                            {currencyFormat(packageObj.chargeAmount)}
                        </Paragraph>

                        <Paragraph size="lg" className="font-normal text-gray-600">
                            {packageObj.statusString}
                        </Paragraph>
                    </div>

                    <Divider className="my-6" />

                    <div className="flex flex-col items-stretch justify-start gap-y-6">
                        {!isWorkflowReviewStep && (
                            <BaseRecordView.IconField
                                icon="hash.large"
                                title={`${packageObj.capacity ?? 0}`}
                                subtitle="Total sessions"
                            />
                        )}

                        {!!packageObj.subaccount && (
                            <BaseRecordView.IconField
                                icon={
                                    <ContactAvatar
                                        user={packageObj.subaccount}
                                        containerClassName="row-span-2"
                                        hasHoverEffect
                                    />
                                }
                                title={packageObj.subaccount.name ?? ''}
                                subtitle={packageObj.subaccount.labelString}
                                href={`/subaccounts/${packageObj.subaccount.id}`}
                            />
                        )}

                        <BaseRecordView.IconField
                            icon={
                                <ContactAvatar
                                    user={client}
                                    hasHoverEffect={!isWorkflowReviewStep}
                                />
                            }
                            title={client.name ?? ''}
                            subtitle="Client"
                            secondaryView={
                                !isWorkflowReviewStep ? (
                                    <a href={`/messages/${client.id}`}>
                                        <Button intent="secondary" size="small">
                                            Chat
                                        </Button>
                                    </a>
                                ) : undefined
                            }
                            href={!isWorkflowReviewStep ? `/clients/${client.id}` : undefined}
                        />
                    </div>
                </Card>

                {!isWorkflowReviewStep && (
                    <Card size="sm" title="Status">
                        <RecordStatusChecklist
                            items={packageObj.statuses.map((status) => ({
                                title: status.title,
                                subtitle: status.date?.format('MMM Do, YYYY [at] h:mm A'),
                                status: status.date !== undefined ? 'checked' : 'empty',
                            }))}
                            className="mt-6"
                        />
                    </Card>
                )}
            </BaseRecordView.LeftColumn>

            <BaseRecordView.RightColumn>
                {bookings !== undefined && (
                    <Card
                        className="flex flex-col items-stretch justify-start gap-y-8 pb-4"
                        title="Sessions"
                        count={bookings.length > 0 ? bookings.length : undefined}
                    >
                        <div>
                            <div className="flex flex-col items-stretch justify-center gap-y-1 rounded-lg border border-gray-300 px-6 py-4">
                                <Heading size="sm" className="text-gray-700">
                                    {numRemainingSessions}
                                </Heading>

                                <Paragraph size="md" className="text-gray-600">
                                    {numRemainingSessions === 1
                                        ? 'Remaining session'
                                        : 'Remaining sessions'}
                                </Paragraph>
                            </div>

                            <div className="mt-4 flex flex-col items-stretch justify-start">
                                {bookings.map((booking) => {
                                    return (
                                        <Link
                                            key={booking.id ?? ''}
                                            to={`/bookings/${booking.id}`}
                                            className="group relative"
                                        >
                                            <div className="absolute top-0 -right-4 bottom-0 -left-4 z-10 rounded-lg group-hover:bg-black/4" />

                                            <div className="relative z-20 flex w-full items-center justify-center gap-x-4 py-4">
                                                <Icon
                                                    icon="calendar.large"
                                                    background="round"
                                                    className="shrink-0"
                                                />

                                                <div className="flex grow flex-col items-stretch justify-start gap-y-1">
                                                    <Label size="md" className="text-gray-700">
                                                        <a
                                                            href={`/bookings/${booking.id}`}
                                                            className="hover:text-gray-700"
                                                        >
                                                            {booking.client?.name}
                                                        </a>
                                                    </Label>

                                                    {booking.date !== undefined &&
                                                        booking.endDate !== undefined && (
                                                            <Paragraph
                                                                size="sm"
                                                                className="text-gray-600"
                                                            >
                                                                {booking.date.isSame(
                                                                    moment(),
                                                                    'year'
                                                                )
                                                                    ? booking.date.format('MMMM D')
                                                                    : booking.date.format(
                                                                          'MMMM D, YYYY'
                                                                      )}{' '}
                                                                at {booking.date.format('h:mm A')}{' '}
                                                                to{' '}
                                                                {booking.endDate.format('h:mm A')}
                                                            </Paragraph>
                                                        )}
                                                </div>

                                                <div className="shrink-0">
                                                    <Tag
                                                        label={booking.statusTag.label}
                                                        intent={booking.statusTag.intent}
                                                        size="sm"
                                                    />
                                                </div>
                                            </div>
                                        </Link>
                                    )
                                })}
                            </div>
                        </div>
                    </Card>
                )}

                {packageObj.subscription === undefined && (
                    <Card title="Payment details">
                        <div className="mt-4">
                            <WorkflowPage.ReceiptView
                                receipt={receipt}
                                transaction={packageObj}
                                modifierGapSize="small"
                            />
                        </div>
                    </Card>
                )}

                {packageObj.subscription === undefined && payments !== undefined && (
                    <BaseRecordView.PaymentsSection payments={payments} />
                )}

                {!isWorkflowReviewStep && (
                    <WorkflowPage.FormAndContractCard
                        form={packageObj.form}
                        contract={packageObj.contract}
                        onOpenForm={
                            packageObj.record
                                ? () => navigate(`/forms/${packageObj.record?.id}`)
                                : undefined
                        }
                        onOpenContract={
                            packageObj.signature
                                ? () =>
                                      window.open(
                                          `${API_BASE}/package/${packageObj.id}/contract`,
                                          '_blank'
                                      )
                                : undefined
                        }
                    />
                )}

                {(packageObj.memo ?? '').trim().length > 0 && (
                    <Card title="Description">
                        <Paragraph className="mt-8 whitespace-pre-wrap text-gray-600">
                            {packageObj.memo}
                        </Paragraph>
                    </Card>
                )}
            </BaseRecordView.RightColumn>
        </BaseRecordView.Container>
    )
}
